import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { TEST_IDS } from "../../constants/testIds";
import { formatInTz } from "../../lib/dates";
import { useAuthStore } from "../../store/authStore";
import {
  Paperclip,
  Download,
  Trash2,
  Upload,
  FileText,
  Image as ImageIcon,
  Eye,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { toast } from "sonner";

const humanSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const IMAGE_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"]);
const PDF_EXT = new Set(["pdf"]);

function kindOf(filename = "") {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (IMAGE_EXT.has(ext)) return "image";
  if (PDF_EXT.has(ext)) return "pdf";
  return "other";
}

function fileIcon(kind) {
  if (kind === "image") return ImageIcon;
  if (kind === "pdf") return FileText;
  return Paperclip;
}

/**
 * Fetches an attachment as a blob and returns a memoizable object URL.
 * Caller MUST revoke the URL when done (or when replaced) to free memory.
 * Surfaces backend error messages that would otherwise be swallowed by
 * axios's blob response type (which returns a Blob instead of a parsed
 * JSON body on non-2xx).
 */
async function fetchBlobUrl(attId) {
  try {
    const res = await api.get(`/attachments/${attId}/download`, {
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  } catch (err) {
    const parsed = await parseBlobError(err);
    throw new Error(parsed);
  }
}

/** Convert axios blob-response error into the real backend detail so
 *  toast + throw don't fall back to a generic "Something went wrong". */
async function parseBlobError(err) {
  const blob = err?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      try {
        const j = JSON.parse(text);
        return j.detail || j.message || text;
      } catch {
        return text || "Dosya indirilemedi";
      }
    } catch {
      return "Dosya indirilemedi";
    }
  }
  return formatApiErrorDetail(err?.response?.data?.detail) || "Dosya indirilemedi";
}

export default function AttachmentsSection({ taskId, tenantTz }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { att, url, kind } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const inputRef = useRef(null);
  const { user: me } = useAuthStore();

  const load = async () => {
    const { data } = await api.get(`/attachments/tasks/${taskId}`);
    setItems(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Revoke any preview URL when the panel closes or attachment changes
  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview?.url]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/attachments/tasks/${taskId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await parseBlobError(err));
    }
  };

  const openPreview = async (att) => {
    const kind = kindOf(att.original_filename);
    if (kind === "other") return; // not previewable
    setPreviewLoading(true);
    try {
      const url = await fetchBlobUrl(att.id);
      setPreview({ att, url, kind });
    } catch (err) {
      // `fetchBlobUrl` throws a plain `Error` whose message already
      // contains the parsed backend detail (see parseBlobError above).
      toast.error(err?.message || "Dosya önizlenemedi");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const remove = async (att) => {
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          <Paperclip className="mr-1 inline h-3 w-3" />
          {items.length} file{items.length === 1 ? "" : "s"}
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={onFile}
            data-testid={TEST_IDS.attachmentInput}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1 border-zinc-300"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid={TEST_IDS.attachmentUploadBtn}
          >
            <Upload className="h-3 w-3" />
            {uploading ? "…" : "Upload"}
          </Button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((a) => {
          const kind = kindOf(a.original_filename);
          const Icon = fileIcon(kind);
          const previewable = kind !== "other";
          return (
            <li
              key={a.id}
              data-testid={TEST_IDS.attachmentRow(a.id)}
              className="group flex items-center gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 transition-colors hover:border-zinc-300"
            >
              <div
                className={[
                  "flex h-9 w-9 items-center justify-center rounded-md",
                  kind === "image"
                    ? "bg-blue-50 text-blue-700"
                    : kind === "pdf"
                      ? "bg-red-50 text-red-700"
                      : "bg-zinc-100 text-zinc-700",
                ].join(" ")}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                {previewable ? (
                  <button
                    type="button"
                    onClick={() => openPreview(a)}
                    data-testid={`attachment-preview-btn-${a.id}`}
                    className="block w-full truncate text-left text-sm font-medium text-zinc-900 hover:underline underline-offset-2 decoration-zinc-400"
                    title="Ön izleme"
                  >
                    {a.original_filename}
                  </button>
                ) : (
                  <div className="truncate text-sm font-medium text-zinc-900">
                    {a.original_filename}
                  </div>
                )}
                <div className="text-xs text-zinc-500">
                  {humanSize(a.size)} · {formatInTz(a.created_at, tenantTz, "DD MMM HH:mm")}
                </div>
              </div>
              {previewable && (
                <button
                  type="button"
                  onClick={() => openPreview(a)}
                  data-testid={`attachment-preview-eye-${a.id}`}
                  title="Ön izleme"
                  disabled={previewLoading}
                  className="text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-40"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => download(a)}
                className="text-zinc-500 hover:text-zinc-900 transition-colors"
                data-testid={TEST_IDS.attachmentDownloadBtn(a.id)}
                title="İndir"
              >
                <Download className="h-4 w-4" />
              </button>
              {a.user_id === me?.id && (
                <button
                  type="button"
                  onClick={() => remove(a)}
                  className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 transition-opacity"
                  data-testid={TEST_IDS.attachmentDeleteBtn(a.id)}
                  title="Sil"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          );
        })}
        {items.length === 0 && (
          <li className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-xs text-zinc-500">
            No files attached
          </li>
        )}
      </ul>

      {/* Preview modal */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent
          data-testid="attachment-preview-modal"
          className="max-w-5xl w-[92vw] p-0 overflow-hidden"
        >
          <DialogHeader className="border-b border-zinc-100 px-5 py-3 flex-row items-center justify-between gap-2 space-y-0">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base">
                {preview?.att?.original_filename}
              </DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {preview && humanSize(preview.att.size)}
                {preview &&
                  ` · ${formatInTz(preview.att.created_at, tenantTz, "DD MMM HH:mm")}`}
              </DialogDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => preview && download(preview.att)}
              className="gap-1"
              data-testid="attachment-preview-download-btn"
            >
              <Download className="h-3.5 w-3.5" />
              İndir
            </Button>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-auto bg-zinc-50">
            {preview?.kind === "image" && (
              <img
                src={preview.url}
                alt={preview.att.original_filename}
                data-testid="attachment-preview-image"
                className="mx-auto block max-h-[75vh] w-auto"
              />
            )}
            {preview?.kind === "pdf" && (
              <iframe
                src={preview.url}
                title={preview.att.original_filename}
                data-testid="attachment-preview-pdf"
                className="h-[80vh] w-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
